/*
 * Donau · Paket – Verhalten des Blocks aus snippets/donau-paket.liquid.
 *  - Variantenwechsel (Basis/Plus/Komplett): Inhalt aus der neu gerenderten Sektion übernehmen.
 *  - Auswahl je Person: „Einzeln gekauft … · Du sparst …“ live aus den gewählten Teilen rechnen.
 *  - Absenden: Pflichtfelder prüfen (das Formular hat novalidate) und _komponenten füllen
 *    (Varianten-ID×Menge für alle Teile), damit das Bestell-Board das Paket zerlegen kann.
 */
if (!customElements.get('donau-paket')) {
  customElements.define(
    'donau-paket',
    class DonauPaket extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.produktInfo = this.closest('product-info') || document;
        this.geldFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';

        this.beiAenderung = (event) => {
          if (!event.target.matches('[data-paket-feld]')) return;
          event.target.closest('.donau-paket__feld')?.classList.remove('donau-paket__feld--fehlt');
          this.rechnen();
          if (this.alleGewaehlt()) this.meldung(false);
        };
        this.addEventListener('change', this.beiAenderung);

        // Capture-Phase am Vorfahren: läuft vor dem Submit-Handler von product-form.js
        this.beiAbsenden = (event) => {
          const form = event.target;
          // form.id ist hier das Eingabefeld name="id" (Variante) – daher getAttribute
          if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.dataset.formId) return;
          if (this.querySelector('[data-paket-unvollstaendig]')) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          if (!this.alleGewaehlt()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.markiereFehlende();
            this.meldung(true);
            return;
          }
          this.meldung(false);
          this.komponentenFuellen();
        };
        this.produktInfo.addEventListener('submit', this.beiAbsenden, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.abmelden = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId !== this.sectionId || !data.html) return;
            const neu = data.html.getElementById(`DonauPaket-${this.sectionId}`);
            const alt = this.querySelector(`#DonauPaket-${this.sectionId}`);
            if (neu && alt) alt.innerHTML = neu.innerHTML;
            this.rechnen();
          });
        }
        this.rechnen();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.beiAenderung);
        this.produktInfo?.removeEventListener('submit', this.beiAbsenden, true);
        if (this.abmelden) this.abmelden();
      }

      felder() {
        return Array.from(this.querySelectorAll('[data-paket-feld]'));
      }

      alleGewaehlt() {
        return this.felder().every((feld) => feld.value !== '');
      }

      markiereFehlende() {
        let erstes = null;
        this.felder().forEach((feld) => {
          const fehlt = feld.value === '';
          feld.closest('.donau-paket__feld')?.classList.toggle('donau-paket__feld--fehlt', fehlt);
          if (fehlt && !erstes) erstes = feld;
        });
        erstes?.focus();
      }

      meldung(zeigen) {
        this.querySelector('[data-paket-meldung]')?.classList.toggle('hidden', !zeigen);
      }

      // Einzelpreis-Summe: feste Teile + je Auswahlfeld die gewählte Option, sonst die günstigste
      rechnen() {
        const vergleich = this.querySelector('[data-paket-vergleich]');
        if (!vergleich) return;
        let summe = 0;
        this.querySelectorAll('[data-fest]').forEach((teil) => {
          summe += Number(teil.dataset.preis) * Number(teil.dataset.menge);
        });
        this.felder().forEach((feld) => {
          const optionen = Array.from(feld.options).filter((o) => o.dataset.preis);
          const gewaehlt = feld.selectedOptions[0];
          if (gewaehlt && gewaehlt.dataset.preis) {
            summe += Number(gewaehlt.dataset.preis);
          } else if (optionen.length) {
            summe += Math.min(...optionen.map((o) => Number(o.dataset.preis)));
          }
        });
        const paketpreis = Number(vergleich.dataset.paketpreis);
        const ersparnis = summe - paketpreis;
        vergleich.classList.toggle('hidden', !(ersparnis > 0));
        const einzeln = vergleich.querySelector('[data-paket-einzeln]');
        const spart = vergleich.querySelector('[data-paket-ersparnis]');
        if (einzeln) einzeln.textContent = this.geld(summe);
        if (spart) spart.textContent = this.geld(ersparnis);
      }

      // Cent-Betrag im Geldformat des Shops (wie der Liquid-Filter money), z. B. „€410,93“
      geld(cent) {
        const tausender = (zahl, trenner) => zahl.replace(/\B(?=(\d{3})+(?!\d))/g, trenner);
        const betrag = (cent / 100).toFixed(2);
        const [ganz, dezimal] = betrag.split('.');
        const werte = {
          amount: `${tausender(ganz, ',')}.${dezimal}`,
          amount_no_decimals: tausender(String(Math.round(cent / 100)), ','),
          amount_with_comma_separator: `${tausender(ganz, '.')},${dezimal}`,
          amount_no_decimals_with_comma_separator: tausender(String(Math.round(cent / 100)), '.'),
        };
        return this.geldFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, art) => werte[art] ?? werte.amount_with_comma_separator);
      }

      // „ID×Menge“ für alle Teile, gleiche IDs zusammengefasst
      komponentenFuellen() {
        const mengen = new Map();
        const dazu = (id, menge) => mengen.set(id, (mengen.get(id) || 0) + menge);
        this.querySelectorAll('[data-fest]').forEach((teil) => dazu(teil.dataset.fest, Number(teil.dataset.menge)));
        this.felder().forEach((feld) => {
          const id = feld.selectedOptions[0]?.dataset.id;
          if (id) dazu(id, 1);
        });
        const feld = this.querySelector('[data-paket-komponenten]');
        if (feld) feld.value = Array.from(mengen, ([id, menge]) => `${id}x${menge}`).join(',');
      }
    }
  );
}
